import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { UserPlus, FileCheck2, CreditCard, ShieldCheck, Zap, BadgeCheck } from "lucide-react";

import { IntlContext } from "context";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import SupportCard from "./SupportCard";
import { cn } from "lib/utils";
import { useReferences } from "hooks/reference";

const SUPPORT_ITEMS = [
    { key: "inscription", icon: UserPlus },
    { key: "legal", icon: FileCheck2 },
    { key: "payments", icon: CreditCard },
    { key: "protected", icon: ShieldCheck },
    { key: "automated", icon: Zap },
    { key: "certified", icon: BadgeCheck },
];

const Support: React.FC<{ className?: string }> = ({ className }) => {
    const intl = useContext(IntlContext);
    const reference = useReferences();

    const serverUrl = process.env.REACT_APP_SERVER_URL ?? "";

    const fileAssurance = reference.get("file", "assurance");
    const urlAssurance = fileAssurance ? `${serverUrl}file/ressource/${fileAssurance.code}` : "#";

    const items = SUPPORT_ITEMS.map(({ key, icon }) => ({
        icon,
        title: intl.formatMessage(`home.support.${key}.title`),
        emphasis: intl.formatMessage(`home.support.${key}.emphasis`),
        description: intl.formatMessage(`home.support.${key}.description`),
    }));

    return (
        <section className={cn("px-10 2xl:px-0", className)}>
            <div className="relative overflow-hidden rounded-3xl bg-primary px-6 py-12 sm:px-10 sm:py-16">
                <span
                    aria-hidden
                    className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary-foreground/10 blur-3xl"
                />
                <span
                    aria-hidden
                    className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-primary-foreground/10 blur-3xl"
                />

                <div className="relative flex flex-col items-center gap-5 text-center text-primary-foreground">
                    <Badge variant="ghost" className="text-primary-foreground">
                        <ShieldCheck aria-hidden />
                        {intl.formatMessage("home.support.badge")}
                    </Badge>

                    <h2 className="text-4xl font-black leading-[0.95] sm:text-5xl">
                        {intl.formatMessage("home.support.title")}{" "}
                        <span className="text-primary-foreground/50">
                            {intl.formatMessage("home.support.title_highlight")}
                        </span>
                    </h2>
                </div>

                <div className="relative mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((item) => (
                        <SupportCard key={item.title} {...item} />
                    ))}
                </div>

                <div className="relative mt-10 flex justify-center">
                    <Button asChild size="lg" variant="white" className="text-primary shadow-xl">
                        <Link to={urlAssurance} target="_blank">{intl.formatMessage("home.support.cta")}</Link>
                    </Button>
                </div>
            </div>
        </section>
    );
};

export default Support;
